/**
 * Spec 051 — error-screen parity, Phase 2 (recover).
 *
 * The single shared recovery treatment (D3) for the four caregiver-facing error
 * screens that spec 049 never touched. Each screen offers a state-changing
 * action (FR-005); a screen whose start can reproduce the failure never offers
 * "home" as its only action (FR-006); an expired/invalid session is recovered by
 * re-authenticating (FR-007); and whether the action SUCCEEDED is made
 * observable (FR-012) by writing a pending record before navigating and letting
 * the app's single `NavigationEnd` hook resolve it.
 *
 * Design constraints honoured here:
 *  - FR-010: every action is wrapped so it can never throw into `ErrorHandler`
 *    and route the caregiver into another error screen. A synchronous,
 *    burst-safe guard is written BEFORE the action so a double-click or a burst
 *    cannot start it twice.
 *  - Native: this introduces NO cache-releasing limb. `retry-reload` is a plain
 *    full-page load (a backend outage is not a stale client shell — stale-build
 *    recovery stays the spec-049 unicorn's guarded cache purge). The only
 *    native-sensitive action, `interactive-reauth`, already routes through
 *    `nativeLogin` (system browser on native), so no new native guard is needed.
 *  - It REUSES spec 049's outcome telemetry conventions via
 *    {@link ErrorScreenTelemetryService} rather than forking a parallel path,
 *    and keeps its own sessionStorage keys so the two paths cannot cross-resolve.
 */
import { DOCUMENT } from '@angular/common';
import {
  Injectable,
  InjectionToken,
  Injector,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  ErrorScreen,
  ErrorScreenPendingRecovery,
  ErrorScreenRecoveryAction,
  ERROR_SCREEN_RECOVERY_GUARD_PREFIX,
  ERROR_SCREEN_RECOVERY_GUARD_TTL_MS,
  getErrorScreenRecoveryGuardKey,
} from 'src/app/core/domain/error-screen';
import { ErrorScreenTelemetryService } from './error-screen-telemetry.service';

/**
 * Kicks off an interactive re-login and resolves to whether it started. Behind
 * an injection token so the error screens keep NO static dependency on Auth0
 * (FR-010 independence) and it can be spied in tests. Mirrors spec 049's
 * `UNICORN_REAUTHENTICATE`; `initiateInteractiveReauth` routes the authorize URL
 * through the system browser on native Capacitor and is a no-op guard on web.
 */
export const ERROR_SCREEN_REAUTHENTICATE = new InjectionToken<
  (destination: string) => Promise<boolean>
>('ERROR_SCREEN_REAUTHENTICATE', {
  providedIn: 'root',
  factory: () => {
    const injector = inject(Injector);
    return async (destination: string): Promise<boolean> => {
      const auth = await import('src/app/core/auth/interactive-reauth');
      const authServiceModule = await import('@auth0/auth0-angular');
      const authService = injector.get(authServiceModule.AuthService);
      auth.initiateInteractiveReauth(authService, destination);
      return true;
    };
  },
});

/** A recovery action the caller wishes to take, with its intended destination. */
export interface ErrorScreenRecoveryRequest {
  screen: ErrorScreen;
  action: ErrorScreenRecoveryAction;
  /** Stable per-screen-instance code; guards and correlates the outcome. */
  incidentCode: string;
  /** Vetted machine-readable arrival reason recorded on the shown event. */
  arrivalReason: string;
  /** Where the action should send the caregiver (SPA route or reload target). */
  destination: string;
}

@Injectable({ providedIn: 'root' })
export class ErrorScreenRecoveryService {
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly telemetry = inject(ErrorScreenTelemetryService);
  private readonly injector = inject(Injector);
  private readonly inProgress = new Set<string>();

  /**
   * Takes a recovery action. Returns `false` when the action was blocked by the
   * one-shot guard (so the caller can surface an "unavailable" message), `true`
   * when it was started. Never throws (FR-010).
   */
  recover(request: ErrorScreenRecoveryRequest): boolean {
    const guardKey = getErrorScreenRecoveryGuardKey(
      request.incidentCode,
      request.action,
    );
    if (this.inProgress.has(guardKey) || !this.persistGuard(guardKey)) {
      return false;
    }
    this.inProgress.add(guardKey);

    const now = this.getNow();
    const pending: ErrorScreenPendingRecovery = {
      incidentCode: request.incidentCode,
      screen: request.screen,
      action: request.action,
      arrivalReason: request.arrivalReason,
      startedAt: now,
      shownAt: now,
      destination: request.destination,
    };

    const measurable = this.telemetry.writePendingRecovery(pending);
    if (!measurable) {
      // The outcome cannot be resolved on landing without a stored record, so
      // record it truthfully as unmeasurable rather than silently dropping it.
      this.telemetry.recordRecoveryOutcome(pending, 'unmeasurable');
    }

    void this.execute(request, pending, measurable);
    return true;
  }

  private async execute(
    request: ErrorScreenRecoveryRequest,
    pending: ErrorScreenPendingRecovery,
    measurable: boolean,
  ): Promise<void> {
    try {
      switch (request.action) {
        case 'go-home':
          // In-app navigation; resolved by the NavigationEnd hook immediately.
          await this.router.navigateByUrl(request.destination);
          return;
        case 'retry-reload':
          // Fresh full-page load re-attempts the failing operation once a
          // transient backend outage clears. NOT a cache purge (native-safe).
          this.navigateDocument(request.destination);
          return;
        case 'interactive-reauth': {
          const reauthenticate = this.injector.get(
            ERROR_SCREEN_REAUTHENTICATE,
          );
          const started = await reauthenticate(request.destination);
          if (started !== true) {
            this.fail(request, pending, measurable);
          }
          return;
        }
      }
    } catch {
      this.fail(request, pending, measurable);
    }
  }

  private fail(
    request: ErrorScreenRecoveryRequest,
    pending: ErrorScreenPendingRecovery,
    measurable: boolean,
  ): void {
    // Let a later attempt retry, and record the failed attempt where it would
    // otherwise never reach a landing to be resolved.
    this.clearGuard(getErrorScreenRecoveryGuardKey(
      request.incidentCode,
      request.action,
    ));
    this.inProgress.delete(
      getErrorScreenRecoveryGuardKey(request.incidentCode, request.action),
    );
    if (measurable) {
      this.telemetry.recordRecoveryOutcome(pending, 'failure');
    }
  }

  private navigateDocument(destination: string): void {
    const location = this.document.defaultView?.location;
    if (!location) {
      throw new Error('document navigation unavailable');
    }
    location.assign(destination);
  }

  private persistGuard(key: string): boolean {
    try {
      const storage = this.getStorage();
      if (!storage) {
        return false;
      }
      const now = this.getNow();
      this.removeExpiredGuards(storage, now);
      const previous = Number(storage.getItem(key));
      if (
        Number.isFinite(previous) &&
        previous > 0 &&
        now >= previous &&
        now - previous < ERROR_SCREEN_RECOVERY_GUARD_TTL_MS
      ) {
        return false;
      }
      const value = String(now);
      storage.setItem(key, value);
      return storage.getItem(key) === value;
    } catch {
      return false;
    }
  }

  private removeExpiredGuards(storage: Storage, now: number): void {
    const expired: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (!key?.startsWith(`${ERROR_SCREEN_RECOVERY_GUARD_PREFIX}.`)) {
        continue;
      }
      const at = Number(storage.getItem(key));
      if (
        !Number.isFinite(at) ||
        at <= 0 ||
        now < at ||
        now - at >= ERROR_SCREEN_RECOVERY_GUARD_TTL_MS
      ) {
        expired.push(key);
      }
    }
    expired.forEach(key => storage.removeItem(key));
  }

  private clearGuard(key: string): void {
    try {
      this.getStorage()?.removeItem(key);
    } catch {
      // A later click stays safely blocked if the guard cannot be cleared.
    }
  }

  private getStorage(): Storage | undefined {
    try {
      return this.document.defaultView?.sessionStorage ?? undefined;
    } catch {
      return undefined;
    }
  }

  private getNow(): number {
    try {
      return Date.now();
    } catch {
      return 0;
    }
  }
}
